/**
 * render.ts — HTML5 canvas renderer for active-perception.
 *
 * Layers, back-to-front each frame:
 *   1. Floor              — dark grid background.
 *   2. Explored tint       — cells the agent has sensed/visited (confident[i]).
 *   3. Belief heat-map     — bGold (warm) / bStone (cool), only on sensed cells.
 *   4. World truth         — gold (value-graded) + stones (cost-graded).
 *   5. Sensor footprint    — the trajectory-coupled affordance: a translucent
 *                            shape extending AHEAD in the facing direction,
 *                            re-orienting when the agent turns.
 *   6. Agent               — disc with heading tick.
 *
 * The sensor footprint (layer 5) is the visual centerpiece of this regime —
 * without it, the demo reads as identical to vacuum-demo. The footprint
 * visibly EXTENDS in the last-move direction and snaps to a new orientation
 * when the agent turns, which is how the viewer perceives "trajectory-coupled
 * anisotropic sensing."
 *
 * Value-graded gold: a cell's gold value is in [0, V_MAX]; the renderer scales
 * coin radius by sqrt(value) so the AREA matches the value (a value-1.0 coin
 * is twice the area of a value-0.5 coin). This is the second affordance — the
 * viewer SEES that some gold is worth more than other gold.
 */

import { World } from './environment';
import { Env, FREE, GOLD, STONE, V_MAX, K_MAX } from './environment';

const FLOOR = '#0b1a24';
const GRID_LINE = 'rgba(255,255,255,0.04)';
const GOLD_COLOR = '#e0b34a';
const GOLD_HIGHLIGHT = '#f7dd92';
const GOLD_SHADOW = '#7a5512';
const GOLD_INNER = '#c08f1a';
const STONE_COLOR = '#4a5b6b';
const STONE_HIGHLIGHT = '#7c93a6';
const STONE_SHADOW = '#28323e';
/** Alpha for unsensed painted cells. Visible enough to read, dim enough that
 *  revealed cells pop. */
const GOLD_FADE = 0.28;
const STONE_FADE = 0.22;
const EXPLORED_TINT = 'rgba(120, 200, 255, 0.05)';
/** Sensor footprint overlay — a soft warm wash that makes the directional
 *  reach obvious. Stronger at the centre, weaker at the edges. */
const SENSOR_FILL = 'rgba(255, 230, 150, 0.10)';
const SENSOR_EDGE = 'rgba(255, 230, 150, 0.30)';
const AGENT_BODY = '#cde8ff';
const AGENT_RING = '#4c72b0';

export interface AgentAnim {
  /** Current pose in cell coords (integer). */
  r: number;
  c: number;
  /** Smoothed pixel position (cells), eased toward (r, c). */
  sr: number;
  sc: number;
  /** Heading in radians, eased toward the last move direction. */
  heading: number;
  targetHeading: number;
  /** Set when the agent bumps; counts down to 0 over a few frames. */
  bumpFlash: number;
}

export function makeAgentAnim(r: number, c: number): AgentAnim {
  return { r, c, sr: r, sc: c, heading: 0, targetHeading: 0, bumpFlash: 0 };
}

export interface RenderState {
  world: World;
  env: Env;
  agent: AgentAnim;
  /** True = show full ground truth everywhere (debug). */
  revealAll: boolean;
  /** True = draw the belief heat-map. */
  showBelief: boolean;
  /** True = draw the sensor footprint overlay. */
  showSensor: boolean;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private bg: HTMLCanvasElement;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas not supported');
    this.ctx = ctx;
    this.bg = document.createElement('canvas');
    this.resize();
  }

  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (w === 0 || h === 0) return;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.renderBackground();
  }

  get widthPx(): number { return this.canvas.getBoundingClientRect().width; }
  get heightPx(): number { return this.canvas.getBoundingClientRect().height; }

  cellPx(state: RenderState): number {
    const g = state.world.grid;
    return Math.min(this.widthPx / g, this.heightPx / g);
  }

  private renderBackground(): void {
    const w = this.widthPx;
    const h = this.heightPx;
    this.bg.width = Math.floor(w);
    this.bg.height = Math.floor(h);
    const ctx = this.bg.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = FLOOR;
    ctx.fillRect(0, 0, w, h);
  }

  /** One frame. `t` is a monotonic clock in ms. */
  frame(_t: number, state: RenderState): void {
    const w = this.widthPx;
    const h = this.heightPx;
    if (w === 0 || h === 0 || this.bg.width === 0 || this.bg.height === 0) {
      this.resize();
      if (this.bg.width === 0 || this.bg.height === 0) return;
    }
    const ctx = this.ctx;
    const g = state.world.grid;
    const cell = this.cellPx(state);
    const ox = (w - cell * g) / 2;
    const oy = (h - cell * g) / 2;

    // Layer 1 — floor.
    ctx.drawImage(this.bg, 0, 0, w, h);

    // Grid lines.
    ctx.strokeStyle = GRID_LINE;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= g; i++) {
      ctx.moveTo(ox + i * cell, oy);
      ctx.lineTo(ox + i * cell, oy + g * cell);
      ctx.moveTo(ox, oy + i * cell);
      ctx.lineTo(ox + g * cell, oy + i * cell);
    }
    ctx.stroke();

    // Layer 2 — explored tint.
    this.drawExplored(state, cell, ox, oy);

    // Layer 3 — belief heat-map.
    if (state.showBelief) this.drawBelief(state, cell, ox, oy);

    // Layer 4 — world truth (value-graded gold, cost-graded stones).
    this.drawWorld(state, cell, ox, oy);

    // Layer 5 — sensor footprint (the regime's defining affordance).
    if (state.showSensor) this.drawSensorFootprint(state, cell, ox, oy);

    // Layer 6 — agent.
    this.drawAgent(state, cell, ox, oy);
  }

  /** Explored cells (confident=1) — a faint cyan tint. */
  private drawExplored(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const g = state.world.grid;
    const conf = state.env.shadow.confident;
    ctx.fillStyle = EXPLORED_TINT;
    for (let i = 0; i < conf.length; i++) {
      if (!conf[i]) continue;
      const r = (i / g) | 0;
      const c = i - r * g;
      ctx.fillRect(ox + c * cell, oy + r * cell, cell, cell);
    }
  }

  /** Belief heat-map: each sensed cell tinted by bGold (warm) / bStone (cool). */
  private drawBelief(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const g = state.world.grid;
    const b = state.env.shadow;
    for (let r = 0; r < g; r++) {
      for (let c = 0; c < g; c++) {
        const i = r * g + c;
        if (!b.confident[i] && b.bGold[i] < 0.02 && b.bStone[i] < 0.02) continue;
        const bd = b.bGold[i];
        const bb = b.bStone[i];
        if (bd < 0.02 && bb < 0.02) continue;
        const px = ox + c * cell;
        const py = oy + r * cell;
        if (bd > 0.02) {
          ctx.fillStyle = `rgba(224, 179, 74, ${0.10 + 0.55 * bd})`;
          ctx.fillRect(px, py, cell, cell);
        }
        if (bb > 0.02) {
          ctx.fillStyle = `rgba(76, 114, 176, ${0.15 + 0.50 * bb})`;
          ctx.fillRect(px, py, cell, cell);
        }
      }
    }
  }

  /** World truth — gold coins (radius scaled by sqrt(value)) and stones
   *  (radius scaled by sqrt(cost/K_MAX)). Painted cells fade unless sensed
   *  (or revealAll is on). */
  private drawWorld(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const g = state.world.grid;
    const cells = state.world.cells;
    const values = state.world.values;
    const conf = state.env.shadow.confident;
    for (let r = 0; r < g; r++) {
      for (let c = 0; c < g; c++) {
        const i = r * g + c;
        const kind = cells[i];
        if (kind === FREE) continue;
        const sensed = conf[i] === 1;
        const fullAlpha = state.revealAll || sensed;
        const px = ox + c * cell;
        const py = oy + r * cell;
        if (kind === GOLD) {
          ctx.globalAlpha = fullAlpha ? 1 : GOLD_FADE;
          this.drawCoin(ctx, px, py, cell, Math.sqrt(values[i] / V_MAX), values[i] / V_MAX);
        } else if (kind === STONE) {
          ctx.globalAlpha = fullAlpha ? 1 : STONE_FADE;
          this.drawStone(ctx, px, py, cell, Math.sqrt(values[i] / K_MAX));
        }
        ctx.globalAlpha = 1;
      }
    }
  }

  /** Coin — radial gradient body. `valueFrac` in [0, 1] scales the radius.
   *  The fraction is also painted inside the coin as a 1-decimal digit so
   *  the user can see WHY the agent prioritises "fat" coins — the radius
   *  encodes it perceptually, the digit encodes it precisely. */
  private drawCoin(
    ctx: CanvasRenderingContext2D,
    px: number, py: number, cell: number,
    valueFrac: number,
    normValue: number,
  ): void {
    const cx = px + cell * 0.5;
    const cy = py + cell * 0.5;
    // Base radius 0.18..0.36 of the cell, scaled by sqrt(value).
    const radius = cell * (0.18 + 0.18 * Math.max(0.1, valueFrac));
    const grad = ctx.createRadialGradient(
      cx - radius * 0.35, cy - radius * 0.35, radius * 0.1,
      cx, cy, radius,
    );
    grad.addColorStop(0, GOLD_HIGHLIGHT);
    grad.addColorStop(0.55, GOLD_COLOR);
    grad.addColorStop(1, GOLD_SHADOW);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = GOLD_SHADOW;
    ctx.lineWidth = Math.max(0.6, cell * 0.04);
    ctx.stroke();
    // Inner ring.
    ctx.strokeStyle = GOLD_INNER;
    ctx.lineWidth = Math.max(0.4, cell * 0.03);
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.62, 0, Math.PI * 2);
    ctx.stroke();
    // Glint.
    ctx.fillStyle = GOLD_HIGHLIGHT;
    ctx.beginPath();
    ctx.arc(cx - radius * 0.38, cy - radius * 0.38, radius * 0.14, 0, Math.PI * 2);
    ctx.fill();

    // Value label — the normalized value in [0,1] with one decimal.
    // Scaled by the coin radius so it stays inside without overflowing.
    // Skip if the coin is too small to render readable text (<9px).
    const fontSize = Math.max(8, Math.round(radius * 0.78));
    if (fontSize >= 9) {
      ctx.font = `600 ${fontSize}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#3a2a05';
      ctx.fillText(normValue.toFixed(1), cx, cy + 0.5);
    }
  }

  /** Stone — irregular polygon. `costFrac` in [0, 1] scales the radius. */
  private drawStone(
    ctx: CanvasRenderingContext2D,
    px: number, py: number, cell: number,
    costFrac: number,
  ): void {
    const cx = px + cell * 0.5;
    const cy = py + cell * 0.5;
    const baseR = cell * (0.20 + 0.22 * Math.max(0.1, costFrac));
    // Deterministic 7-vertex polygon from cell index (no RNG object needed).
    let s = ((cx * 31 + cy * 17) | 0) >>> 0;
    const rand = () => {
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const nVerts = 7;
    ctx.beginPath();
    for (let k = 0; k < nVerts; k++) {
      const ang = (k / nVerts) * Math.PI * 2;
      const rj = baseR * (0.8 + rand() * 0.4);
      const vx = cx + Math.cos(ang) * rj;
      const vy = cy + Math.sin(ang) * rj;
      if (k === 0) ctx.moveTo(vx, vy);
      else ctx.lineTo(vx, vy);
    }
    ctx.closePath();
    const grad = ctx.createLinearGradient(
      cx - baseR, cy - baseR, cx + baseR, cy + baseR,
    );
    grad.addColorStop(0, STONE_HIGHLIGHT);
    grad.addColorStop(0.5, STONE_COLOR);
    grad.addColorStop(1, STONE_SHADOW);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = STONE_SHADOW;
    ctx.lineWidth = Math.max(0.5, cell * 0.03);
    ctx.stroke();
  }

  /** The sensor footprint — a translucent warm wash over the cells the agent
   *  currently senses. THIS IS THE REGIME'S DEFINING AFFORDANCE: the shape is
   *  anisotropic (extends further ahead than to the sides) and re-orients
   *  every time the agent changes direction. */
  private drawSensorFootprint(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const fp = state.env.footprint;
    if (fp.length === 0) return;
    // Fill each footprinted cell with the sensor wash.
    ctx.fillStyle = SENSOR_FILL;
    for (const { r, c } of fp) {
      ctx.fillRect(ox + c * cell, oy + r * cell, cell, cell);
    }
    // Outline the footprint as a polygon (convex-hull-ish: just trace the
    // cell rects). For simplicity, stroke each cell's perimeter faintly.
    ctx.strokeStyle = SENSOR_EDGE;
    ctx.lineWidth = 1;
    for (const { r, c } of fp) {
      ctx.strokeRect(ox + c * cell + 0.5, oy + r * cell + 0.5, cell - 1, cell - 1);
    }
  }

  /** Agent disc + heading + bump flash. Eases between cells. */
  private drawAgent(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const a = state.agent;
    const ease = 0.25;
    a.sr += (a.r - a.sr) * ease;
    a.sc += (a.c - a.sc) * ease;
    // Heading — only ease toward targetHeading when an actual move happened
    // (otherwise the tick flicks around when the agent is bumping in place).
    if (state.env.facing !== null) {
      let dh = a.targetHeading - a.heading;
      while (dh > Math.PI) dh -= 2 * Math.PI;
      while (dh < -Math.PI) dh += 2 * Math.PI;
      a.heading += dh * 0.3;
    }
    if (a.bumpFlash > 0) a.bumpFlash = Math.max(0, a.bumpFlash - 0.04);

    const px = ox + (a.sc + 0.5) * cell;
    const py = oy + (a.sr + 0.5) * cell;
    const radius = cell * 0.34;

    // Bump flash.
    if (a.bumpFlash > 0) {
      ctx.strokeStyle = `rgba(196, 78, 82, ${a.bumpFlash})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(px, py, radius * (1.4 + (1 - a.bumpFlash) * 0.8), 0, Math.PI * 2);
      ctx.stroke();
    }

    // Body.
    ctx.fillStyle = AGENT_BODY;
    ctx.strokeStyle = AGENT_RING;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Heading tick — drawn even on the first step (targetHeading starts at 0,
    // which renders as East; this is a cosmetic default, not a real facing).
    ctx.strokeStyle = AGENT_RING;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(
      px + Math.cos(a.heading) * radius,
      py + Math.sin(a.heading) * radius,
    );
    ctx.stroke();
  }
}
